/**
 * Source-document classify and artifact-open actions from Property Detail.
 */
import { authenticatedFetch, DashboardAuthRequiredError } from "./dashboard-auth.js";
import type { DashboardActionHost } from "./dashboard-referenced-deed-actions.js";
import { leadFactoryDocumentClasses } from "./dashboard-lead-factory-constants.js";

export function bindSourceDocumentPreviewActions(statusElement: HTMLElement, actions: DashboardActionHost): void {
  document.querySelectorAll<HTMLButtonElement>("[data-source-document-preview]").forEach((button) => {
    button.addEventListener("click", () => {
      const sourceDocumentId = button.dataset.sourceDocumentPreview;
      if (!sourceDocumentId) return;
      void openSourceDocumentArtifact(sourceDocumentId, statusElement, actions);
    });
  });
}

export function bindSourceDocumentClassificationActions(statusElement: HTMLElement, actions: DashboardActionHost): void {
  document.querySelectorAll<HTMLButtonElement>("[data-source-document-classify]").forEach((button) => {
    button.addEventListener("click", () => {
      const sourceDocumentId = button.dataset.sourceDocumentClassify;
      if (!sourceDocumentId) return;
      void submitSourceDocumentClassification(
        sourceDocumentId,
        button.dataset.sourceDocumentClass || "unknown",
        statusElement,
        actions
      );
    });
  });
}

export async function submitSourceDocumentClassification(
  sourceDocumentId: string,
  currentClass: string,
  statusElement: HTMLElement,
  actions: DashboardActionHost
): Promise<void> {
  const documentClass = window.prompt(
    `Classify ${sourceDocumentId} as:\n${leadFactoryDocumentClasses.join(", ")}`,
    currentClass || "unknown"
  );
  const normalizedClass = documentClass?.trim() || "";
  if (!normalizedClass) {
    statusElement.textContent = "Document classification cancelled; no class entered.";
    return;
  }
  if (!leadFactoryDocumentClasses.includes(normalizedClass as (typeof leadFactoryDocumentClasses)[number])) {
    statusElement.textContent = `${normalizedClass} is not a known document class.`;
    return;
  }
  statusElement.textContent = `${sourceDocumentId}: saving document classification...`;
  try {
    const response = await authenticatedFetch(
      `/dashboard/lead-factory/source-documents/${encodeURIComponent(sourceDocumentId)}/classify`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          documentClass: normalizedClass,
          evidence: "Operator classified from Lead Factory dashboard.",
          note: "Operator reviewed source document classification from Property Detail."
        })
      }
    );
    const body = (await response.json()) as { error?: string };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${sourceDocumentId}: classified as ${normalizedClass}. Reopen detail to refresh.`;
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Document classification failed.";
  }
}

export async function openSourceDocumentArtifact(
  sourceDocumentId: string,
  statusElement: HTMLElement,
  actions: DashboardActionHost
): Promise<void> {
  statusElement.textContent = `${sourceDocumentId}: opening source PDF...`;
  try {
    const response = await authenticatedFetch(
      `/dashboard/lead-factory/source-documents/${encodeURIComponent(sourceDocumentId)}/artifact`
    );
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { error?: string };
      throw new Error(body.error ?? `PDF request failed with ${response.status}`);
    }
    const objectUrl = URL.createObjectURL(await response.blob());
    const opened = window.open(objectUrl, "_blank", "noopener");
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    statusElement.textContent = opened
      ? `${sourceDocumentId}: source PDF opened.`
      : `${sourceDocumentId}: PDF is ready, but the browser blocked the new tab.`;
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Source PDF failed.";
  }
}
